import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, User, X } from 'lucide-react';
import logger from '../../utils/logger';

interface TeamMemberDropdownFilterProps {
  members: any[];
  selectedValue: string | string[]; // Support both single and multiple
  onSelectionChange?: (value: string | string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  multiple?: boolean; // Enable multi-select mode
}

const TeamMemberDropdownFilter: React.FC<TeamMemberDropdownFilterProps> = ({
  members,
  selectedValue,
  onSelectionChange,
  disabled = false,
  placeholder = "All Team Members",
  style = {},
  multiple = false
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter members based on search term
  const filteredMembers = useMemo(() => {
    if (!searchTerm) return members;
    return members.filter((member: any) =>
      (member.firstName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (member.lastName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (member.email?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );
  }, [members, searchTerm]);

  // Show search box only if more than 10 items
  const showSearch = members.length > 10;

  // Get selected values as array
  const selectedArray = useMemo(() => {
    return Array.isArray(selectedValue) ? selectedValue : (selectedValue ? [selectedValue] : []);
  }, [selectedValue]);

  // Handle selection
  const handleSelect = (memberId: string) => {
    if (multiple) {
      // Multi-select mode
      const newSelection = selectedArray.includes(memberId)
        ? selectedArray.filter(id => id !== memberId)
        : [...selectedArray, memberId];

      if (onSelectionChange) {
        onSelectionChange(newSelection);
      }
      logger.log('👤 Team member multi-selection changed:', newSelection);
    } else {
      // Single select mode
      if (onSelectionChange) {
        onSelectionChange(memberId);
      }
      setIsOpen(false);
      setSearchTerm('');
      logger.log('👤 Team member selection changed:', memberId);
    }
  };

  // Clear selection
  const handleClear = () => {
    if (onSelectionChange) {
      onSelectionChange(multiple ? [] : '');
    }
    logger.log('👤 Cleared team member selection');
  };

  // Get display text for the dropdown button
  const getDisplayText = () => {
    if (selectedArray.length === 0) return placeholder;

    if (multiple) {
      if (selectedArray.length === 1) {
        const selected = members.find((m: any) => m._id === selectedArray[0]);
        if (selected) {
          return `${selected.firstName || ''} ${selected.lastName || ''}`.trim() || selected.email || 'Unknown';
        }
      }
      return `${selectedArray.length} members selected`;
    } else {
      const selected = members.find((m: any) => m._id === selectedValue);
      if (selected) {
        return `${selected.firstName || ''} ${selected.lastName || ''}`.trim() || selected.email || 'Unknown';
      }
    }

    return placeholder;
  };

  // Default styles matching existing dashboard filters
  const defaultStyle = {
    backgroundColor: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    height: "36px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#334155",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    cursor: disabled ? "not-allowed" : "pointer",
    minWidth: "180px",
    position: "relative" as const,
    ...style
  };

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          ...defaultStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 12px',
          border: isOpen ? '1px solid #3b82f6' : defaultStyle.border,
          opacity: disabled ? 0.5 : 1
        }}
        onMouseEnter={(e) => {
          if (!disabled && !isOpen) {
            e.currentTarget.style.borderColor = '#3b82f6';
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.borderColor = '#e2e8f0';
          }
        }}
      >
        <div className="d-flex align-items-center" style={{ minWidth: 0, flex: 1 }}>
          <User size={16} style={{ marginRight: '8px', color: '#6b7280', flexShrink: 0 }} />
          <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: selectedValue ? '#334155' : '#6b7280'
          }}>
            {getDisplayText()}
          </span>
          {selectedArray.length > 0 && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              style={{
                marginLeft: '8px',
                padding: '2px',
                background: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '2px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <X size={12} />
            </span>
          )}
        </div>
        <ChevronDown
          size={16}
          style={{
            color: '#6b7280',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            flexShrink: 0
          }}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            zIndex: 1050,
            marginTop: '4px',
            maxHeight: '500px', // BIGGER - show more items without scroll
            overflowY: 'auto',
            minWidth: '100%'
          }}
        >
          {/* Search Input - Only show if > 10 items */}
          {showSearch && (
            <div style={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
              <input
                type="text"
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }}
              />
            </div>
          )}

          {/* Options List */}
          <div style={{ maxHeight: showSearch ? '420px' : '460px', overflowY: 'auto' }}>
            {filteredMembers.length === 0 ? (
              <div style={{
                padding: '12px',
                textAlign: 'center',
                color: '#6b7280',
                fontSize: '13px'
              }}>
                {searchTerm ? 'No members match your search' : 'No members available'}
              </div>
            ) : (
              filteredMembers.map((member: any) => {
                const displayName = `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.email || 'Unknown';
                const isSelected = selectedArray.includes(member._id);

                return (
                  <div
                    key={member._id}
                    onClick={() => handleSelect(member._id)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderBottom: '1px solid #f1f5f9',
                      backgroundColor: isSelected ? '#eff6ff' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        isSelected ? '#eff6ff' : 'transparent';
                    }}
                  >
                    {multiple && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}} // Handled by parent div onClick
                        style={{
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer',
                          flexShrink: 0
                        }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: '#334155',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {displayName}
                      </div>
                      {member.email && displayName !== member.email && (
                        <div style={{
                          fontSize: '11px',
                          color: '#6b7280',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {member.email}
                        </div>
                      )}
                    </div>
                    {!multiple && isSelected && (
                      <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#3b82f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '10px',
                        flexShrink: 0
                      }}>
                        ✓
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMemberDropdownFilter;
